/// <amd-module name="Analytics/Shared/Visuals/AreaChartView/Common/AreaChartHelper" />
import { ChartSeries } from "Analytics/Shared/ChartSeries/ChartSeries";
import { PassPercentSeries } from "Analytics/Shared/ChartSeries/PassPercentSeries";
import { gridLineColor } from "Analytics/Shared/Common/Constants";
import { adjustDataForAllPeriod, IDataForDate } from "Analytics/Shared/Common/Utils/DateAdjuster";
import { ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { IAreaChartAggregateData } from "Analytics/Shared/Visuals/AreaChartView/Common/Types";
import * as Resources from "Analytics/Shared/Visuals/AreaChartView/Resources";
import { ChartTypesConstants, DataSeries, LabelFormatModes } from "VSS/Features/Charts/Contracts";
import { AreaChartOptions, AxisOptions, ChartHostOptions, ChartProps } from "VSS/Features/Charts/Contracts";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { DataPoint, TooltipLineItem } from "VSS/Features/Charts/Contracts";
import * as ChartDataConverter from "Analytics/Shared/Common/Utils/ChartDataConverter";

export class AreaChartHelper {
    private chartProps: ChartProps;
    public getAreaChartOptions(
        context: IVssComponentContext,
        chartData: IAreaChartAggregateData[],
        chartSeries: ChartSeries,
        _enableCompactMode?: boolean | undefined,
        configuration?: ITrendChartReportConfiguration | undefined
    ): ChartProps {
        let xAxisLabelValues: string[] = [];
        const dataSeries: DataSeries[] = [];
        if (configuration) {
            let adjustableData = chartData.map(value => {
                return {
                    date: value.date,
                    data: ChartDataConverter.toTwoDecimalPoint(value.aggrValue)
                } as IDataForDate<number>;
            });

            adjustableData = adjustDataForAllPeriod(adjustableData, configuration.trendBy, configuration.period, undefined);
            chartData = adjustableData.map(value => {
                return {
                    date: value.date,
                    aggrValue: value.data
                } as IAreaChartAggregateData;
            });
        }
        dataSeries.push({
            data: chartData.map(d => (d.aggrValue !== undefined ? d.aggrValue : null)),
            color: chartSeries.color
        });
        if (xAxisLabelValues.length == 0 && chartData) {
            xAxisLabelValues = chartData.map(d => d.date);
        }

        this.chartProps = {
            pageContext: context.pageContext,
            title: Resources.AreaChartTitle,
            chartType: ChartTypesConstants.Area,
            xAxis: {
                labelFormatMode: LabelFormatModes.DateTime_DayInMonth,
                labelsEnabled: true,
                labelValues: xAxisLabelValues,
                formatAccessibleLabelsUsingAxisCategories: true
            } as AxisOptions,
            yAxis: {
                labelsEnabled: true,
                markingsEnabled: false,
                title: chartSeries.name.toUpperCase(),
                max: chartSeries.maxValue,
                gridLineOptions: {
                    enabled: true,
                    gridLineColor: gridLineColor
                }
            },
            suppressAnimation: true,
            series: dataSeries,
            specializedOptions: {
                gradientFill: this.getGradientFill(chartSeries.color, 0.1, 0.1),
                includeMarkers: true,
                lineWidth: 2,
                disableStacking: true
            }
        } as ChartProps;

        if (_enableCompactMode) {
            this.enableCompactMode();
        }

        return this.chartProps;
    }

    public setSecondaryAxisLabelFormatMode(
        chartProps: ChartProps,
        secondaryAxisLabelFormatMode: LabelFormatModes,
        chartSeries: ChartSeries,
        chartYAxisTitle?: string
    ): ChartProps {
        const max = this.getMaxSecondaryYAxisLabel(secondaryAxisLabelFormatMode, chartSeries);
        if (chartProps.yAxis) {
            chartProps.yAxis.labelFormatMode = secondaryAxisLabelFormatMode;
            chartProps.yAxis.max = max;
        } else {
            chartProps.yAxis = {
                labelFormatMode: secondaryAxisLabelFormatMode,
                max: max
            };
        }
        if (chartYAxisTitle) {
            chartProps.yAxis.title = chartYAxisTitle;
        }
        return chartProps;
    }

    public setGradientFill(chartProps: ChartProps, color: string): ChartProps {
        const gradientFill = this.getGradientFill(color, 0.05, 0);
        if (chartProps.specializedOptions) {
            (chartProps.specializedOptions as AreaChartOptions)!.gradientFill = gradientFill;
        } else {
            chartProps.specializedOptions = {
                gradientFill: gradientFill
            } as AreaChartOptions;
        }
        return chartProps;
    }

    public setHostOptions(chartProps: ChartProps, hostOptions: ChartHostOptions): ChartProps {
        chartProps.hostOptions = hostOptions;
        return chartProps;
    }

    public setCustomTooltipMapping(chartProps: ChartProps, tooltipMapping: (points: DataPoint[]) => TooltipLineItem[]) {
        chartProps.tooltip = {
            customTooltipMapping: tooltipMapping
        };
        return chartProps;
    }

    public setAreaChartTitle(chartProps: ChartProps, title: string): ChartProps {
        chartProps.title = title;
        return chartProps;
    }
    
    private getMaxSecondaryYAxisLabel(labelFormatMode: LabelFormatModes, chartSeries?: ChartSeries): number | undefined {
        if (labelFormatMode !== LabelFormatModes.Percentage || !chartSeries) {
            return undefined;
        }

        const seriesValues = chartSeries.values.filter((value: number) => {
            return value !== null && value !== undefined;
        });

        const maxSeriesValue = Math.max(...seriesValues);
        // Limit max secondary y axis label to 100, if data set is greater than 80.
        return maxSeriesValue >= 80 ? (chartSeries as PassPercentSeries).maxValue : undefined;
    }

    private enableCompactMode() {
        this.chartProps.xAxis = {
            labelsEnabled: false,
            enabled: false
        };
        this.chartProps.yAxis = {
            labelsEnabled: false,
            enabled: false
        };
        this.chartProps.legend = {
            enabled: false
        };
        this.chartProps.tooltip = {
            enabled: false
        };
        this.chartProps.suppressMargin = true;
        (this.chartProps.specializedOptions as AreaChartOptions)!.includeMarkers = false;
        if (!this.chartProps.hostOptions) {
            this.chartProps.hostOptions = {
                height: 150
            } as ChartHostOptions;
        }
        this.chartProps.accessibility = {
            enabled: false
        };
    }

    private getGradientFill(color: string, startOpacity: number, endOpacity: number) {
        return {
            linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
            stops: [
                {
                    position: 0,
                    color: color,
                    opacity: startOpacity
                },
                {
                    position: 1,
                    color: color,
                    opacity: endOpacity
                }
            ]
        };
    }
}
